/**
 * @fileoverview Own one lead's workspace and coordination of explicit staff actions.
 *
 * Overview supplies lead/duplicate/decision context; pipeline supplies execution
 * and recommendation context. Timeline, documents, outreach, and workflow have
 * independent loading/refresh ownership. AI advice and staff decisions remain
 * separate facts.
 *
 * `intakeApi.js` owns HTTP operations; `IntakeTabs.js` owns panel composition;
 * `intakeWorkspaceState.js` owns navigation memory. `IntakeCommunications.js` owns
 * the separate conversation review desk.
 * `kc-internal-backend/services/intake/projections.py` owns read-model boundaries;
 * `kc-internal-backend/services/intake/workflow/__init__.py` owns durable execution.
 * Actions reconcile the read models they affect. Refreshing a view must not execute
 * an automation, and stale responses must not replace the selected lead.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppModal, AppSpinner } from '../shared';
import { AuthContext } from '../../AuthContext';
import ToastContext from '../../ToastContext';
import {
  fetchIntakeLeadOverview,
  fetchIntakeLeadPipeline,
  fetchIntakeAttorneyReviewHandoff,
  confirmIntakeAttorneyReviewHandoff,
  mergeIntakeDuplicateCandidate,
  overrideIntakeEngagementPackage,
  reingestIntakeLeadFromPipedrive,
  resetIntakeLeadPipeline,
  resolveIntakeDuplicateCandidate,
  runDuplicateCandidateAgentReview,
  sendIntakeOutreach,
  updateIntakeOutreach
} from './intakeApi';
import { SelectInput, TextArea, TextInput } from './IntakeDetailPrimitives';
import { ProspectPanel, IntakeStatusPanel, PipelineStatusPanel } from './IntakeLeadPanels';
import IntakeTabs, { DecisionActionButtons, IntakeDecisionModal } from './IntakeTabs';
import {
  getDuplicateStatusClass,
  getDuplicateStatusLabel,
  getPipedriveRecordUrl,
  getRunTime,
  getStageClass,
  getStatusClass,
  getTimestamp,
  titleize
} from './intakeFormatters';
import { getOutreachDeliveryNotice, getOutreachSendToast } from './intakeOutreach';
import { ACTIVE_AGENT_STATUSES, COMPLETED_AGENT_STATUSES, FAILED_AGENT_STATUSES, UNRESOLVED_DUPLICATE_STATUSES } from './intakeStatuses';
import { getLastIntakeWorkspacePath } from './intakeWorkspaceState';
import { getLeadPresentationState, isResolvedIntakeLead } from './Queue/intakeQueueUtils';
import './Intake.css';
import AppIcon from "../shared/AppIcon";

const DEFAULT_RESET_REASON = 'Manual pipeline reset from lead detail.';
const RESET_HOLD_REASON = 'Held after manual reset';
const RESET_CLEAR_ITEMS = [
  'Current pipeline, stage, and agent runs',
  'Pending pipeline automation, review gates, and wait timers',
  'Unsent outreach drafts',
  'Research tasks and draft recommendations',
  'Pending attorney review assignments',
  'Agent-derived lead categories and decisions',
  'Reviewed document status'
];
const RESET_KEEP_ITEMS = [
  'Source events and Pipedrive notes',
  'Prospect and defendant lead fields',
  'Sent outreach audit history',
  'Uploaded document files and links',
  'Completed attorney review decisions'
];
const PIPEDRIVE_HANDOFF_MODES = new Set([
  'engagement_letter_handoff',
  'pipedrive_won_handoff',
  'pipedrive_evaluation_documents_handoff'
]);



function isPipedriveHandoffMode(mode) {
  return PIPEDRIVE_HANDOFF_MODES.has(mode);
}



function getPipelineVersionLabel(version) {
  if (!version) return 'latest available pipeline version';
  const versionPart = version.version_number === null || version.version_number === undefined ? '' : `v${version.version_number}`;
  return [versionPart, version.name].filter(Boolean).join(' - ') || 'latest available pipeline version';
}



function getConversionBlocker(preview, convertedMatter, loading, error) {
  if (convertedMatter) return 'Lead already converted.';
  if (loading) return 'Loading conversion target.';
  if (error) return error;
  if (!preview) return 'Conversion target unavailable.';
  if (!isPipedriveHandoffMode(preview.conversion_mode) && preview.exists) return 'Generated matter number already exists.';
  if (preview.missing_fields?.length) return `Missing ${preview.missing_fields.join(', ')}.`;
  if (!preview.can_convert) return 'Lead cannot be converted yet.';
  return '';
}



function upsertById(items = [], nextItem = null) {
  if (!nextItem || nextItem.id === null || nextItem.id === undefined) return items || [];
  const nextId = String(nextItem.id);
  let replaced = false;
  const nextItems = (items || []).map((item) => {
    if (String(item?.id) !== nextId) return item;
    replaced = true;
    return { ...item, ...nextItem };
  });
  return replaced ? nextItems : [...nextItems, nextItem];
}



function mergeRunResponseIntoPayload(current, responsePayload = {}) {
  if (!current) return current;
  const next = { ...current };
  if (responsePayload.lead) {
    next.lead = { ...current.lead, ...responsePayload.lead };
  }
  if (responsePayload.pipeline_run) {
    next.pipeline_runs = upsertById(current.pipeline_runs, responsePayload.pipeline_run);
  }
  if (responsePayload.stage_run) {
    next.stage_runs = upsertById(current.stage_runs, responsePayload.stage_run);
  }
  if (responsePayload.agent_run) {
    next.agent_runs = upsertById(current.agent_runs, responsePayload.agent_run);
  }
  return next;
}




function getLatestDedupeAgentRun(agentRuns = []) {
  return (agentRuns || [])
  .filter((run) => run?.agent_key === 'dedupe' || run?.agent_stage_key === 'ingest_dedupe')
  .sort((left, right) => {
    const timeDiff = getTimestamp(getRunTime(right)) - getTimestamp(getRunTime(left));
    return timeDiff || (right.id || 0) - (left.id || 0);
  })[0] || null;
}



function getLatestDedupeAgentRunForCandidate(agentRuns = [], candidateId) {
  return (agentRuns || [])
  .filter((run) => (run?.agent_key === 'dedupe' || run?.agent_stage_key === 'ingest_dedupe') && String(run?.duplicate_candidate_id || '') === String(candidateId || ''))
  .sort((left, right) => {
    const timeDiff = getTimestamp(getRunTime(right)) - getTimestamp(getRunTime(left));
    return timeDiff || (right.id || 0) - (left.id || 0);
  })[0] || null;
}



function getUnresolvedDuplicateRows(candidates = []) {
  return (candidates || []).filter((row) => UNRESOLVED_DUPLICATE_STATUSES.has(row.status));
}



function getDedupeDisplayState({ candidates = [], dedupeAgentStatus = null, agentRuns = [], agentOutcomes = {} }) {
  const rows = getUnresolvedDuplicateRows(candidates);
  if (!rows.length) return 'no_candidates';
  const rowOutcomes = rows.map((row) => agentOutcomes[row.id]).filter(Boolean);
  const failedOutcome = rowOutcomes.find((outcome) => FAILED_AGENT_STATUSES.has(outcome.status));
  if (failedOutcome) return 'duplicate_resolution_required_agent_failed';
  const completedOutcome = rowOutcomes.find((outcome) => COMPLETED_AGENT_STATUSES.has(outcome.status));
  const activeOutcome = rowOutcomes.find((outcome) => ACTIVE_AGENT_STATUSES.has(outcome.status));
  if (activeOutcome) return 'duplicate_resolution_required_agent_running';
  const latestRun = getLatestDedupeAgentRun(agentRuns);
  if (latestRun && FAILED_AGENT_STATUSES.has(latestRun.status)) return 'duplicate_resolution_required_agent_failed';
  if (dedupeAgentStatus?.available === false) return 'duplicate_resolution_required_agent_unavailable';
  if (completedOutcome || (latestRun && COMPLETED_AGENT_STATUSES.has(latestRun.status))) return 'duplicate_resolution_required_agent_completed';
  if (latestRun && ACTIVE_AGENT_STATUSES.has(latestRun.status)) return 'duplicate_resolution_required_agent_running';
  return 'duplicate_resolution_required_agent_ready';
}



function getDuplicateCountLabel(rows, likelyCount) {
  if (likelyCount) return `${likelyCount} likely duplicate${likelyCount === 1 ? '' : 's'} found`;
  return `${rows.length} possible duplicate${rows.length === 1 ? '' : 's'} found`;
}



function getDedupeStateDetail(displayState, rows, likelyCount, dedupeUnavailableReason, latestRun) {
  const countLabel = getDuplicateCountLabel(rows, likelyCount);
  if (displayState === 'duplicate_resolution_required_agent_unavailable') {
    return `${countLabel}. Dedupe agent unavailable: ${dedupeUnavailableReason}. Choose Not Duplicate or Merge for every candidate to continue.`;
  }
  if (displayState === 'duplicate_resolution_required_agent_failed') {
    const error = latestRun?.error || 'The dedupe agent did not complete.';
    return `${countLabel}. Dedupe Agent Failed: ${error}. Choose Not Duplicate or Merge manually, or retry the agent.`;
  }
  if (displayState === 'duplicate_resolution_required_agent_completed') {
    return `${countLabel}. Dedupe agent completed. Use the result as advisory evidence, then choose Not Duplicate or Merge for every candidate.`;
  }
  if (displayState === 'duplicate_resolution_required_agent_running') {
    return `${countLabel}. Dedupe agent review is queued or running. You can still choose Not Duplicate or Merge manually.`;
  }
  return `${countLabel}. Run the dedupe agent for advisory help, or choose Not Duplicate or Merge for every candidate.`;
}



function getAgentOutcomeText(outcome) {
  if (!outcome) return '';
  if (FAILED_AGENT_STATUSES.has(outcome.status)) return `Dedupe Agent Failed: ${outcome.error || 'The agent did not complete.'}`;
  if (COMPLETED_AGENT_STATUSES.has(outcome.status)) return 'Dedupe agent completed. Advisory only; choose Not Duplicate or Merge.';
  if (ACTIVE_AGENT_STATUSES.has(outcome.status)) return `Dedupe agent ${titleize(outcome.status)}.`;
  return `Dedupe agent ${titleize(outcome.status)}.`;
}



function DuplicateCandidatesPanel({ lead, candidates, dedupeAgentStatus, agentRuns = [], onLeadUpdated, onRefreshLead }) {
  const showToast = useContext(ToastContext);
  const dedupeUnavailableReason = dedupeAgentStatus?.reason || 'Dedupe agent is not available.';
  const [agentReviewId, setAgentReviewId] = useState(null);
  const [agentOutcomes, setAgentOutcomes] = useState({});
  const [mergeCandidate, setMergeCandidate] = useState(null);
  const [mergeReason, setMergeReason] = useState('');
  const [mergeNotes, setMergeNotes] = useState('');
  const [merging, setMerging] = useState(false);
  const [clearingId, setClearingId] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const rows = getUnresolvedDuplicateRows(candidates);
  if (!rows.length) return null;
  const likelyCount = rows.filter((row) => row.status === 'likely_duplicate').length;
  const latestDedupeRun = getLatestDedupeAgentRun(agentRuns);
  const latestLocalOutcome = rows.map((row) => agentOutcomes[row.id]).filter(Boolean).find((outcome) => FAILED_AGENT_STATUSES.has(outcome.status)) || null;
  const displayState = getDedupeDisplayState({ candidates, dedupeAgentStatus, agentRuns, agentOutcomes });
  const canRunDedupeAgent = displayState !== 'duplicate_resolution_required_agent_unavailable';
  const stateDetail = getDedupeStateDetail(displayState, rows, likelyCount, dedupeUnavailableReason, latestLocalOutcome || latestDedupeRun);

  const requestAgentReview =

  async (row) => {
    setAgentReviewId(row.id);
    setAgentOutcomes((current) => ({
      ...current,
      [row.id]: { status: 'running' }
    }));
    try {
      const response = await runDuplicateCandidateAgentReview(lead.id, row.id, {
        reason: `Review duplicate candidate lead #${row.candidate_lead_id}`
      });
      if (response.data.lead) onLeadUpdated(response.data.lead);
      const status = response?.data?.run_result?.status || response?.data?.agent_run?.status;
      if (['failed', 'error'].includes(status)) {
        const error = response?.data?.agent_run?.error || response?.data?.run_result?.error || 'Dedupe agent review failed';
        setAgentOutcomes((current) => ({
          ...current,
          [row.id]: { status: 'failed', error }
        }));
        showToast({ severity: 'error', summary: 'Dedupe Agent Failed', detail: error });
      } else if (ACTIVE_AGENT_STATUSES.has(status)) {
        setAgentOutcomes((current) => ({
          ...current,
          [row.id]: { status }
        }));
        showToast({ severity: 'info', summary: 'Dedupe Agent Queued', detail: response?.data?.run_result?.message || 'Dedupe agent review queued.' });
      } else {
        setAgentOutcomes((current) => ({
          ...current,
          [row.id]: { status: 'completed' }
        }));
        showToast({ severity: 'success', summary: 'Dedupe Agent Completed', detail: 'Use the result as advisory evidence, then choose Not Duplicate or Merge.' });
      }
      if (onRefreshLead) onRefreshLead();
    } catch (error) {
      console.error('Failed to run dedupe agent review:', error);
      const unavailable = error?.response?.data?.dedupe_agent_status?.available === false;
      const detail = error?.response?.data?.error || 'Failed to run dedupe agent review';
      setAgentOutcomes((current) => ({
        ...current,
        [row.id]: { status: 'failed', error: detail }
      }));
      showToast({ severity: 'error', summary: unavailable ? 'Dedupe Agent Unavailable' : 'Dedupe Agent Failed', detail });
    } finally {
      if (mountedRef.current) setAgentReviewId(null);
    }
  };

  const openMergeModal =

  (row) => {
    const candidate = row.candidate_lead || {};
    const label = candidate.prospect_name || candidate.lead_title || `Lead #${row.candidate_lead_id}`;
    setMergeCandidate(row);
    setMergeReason(`Duplicate of ${label}`);
    setMergeNotes('');
  };

  const confirmMerge =

  async () => {
    if (!mergeCandidate) return;
    setMerging(true);
    try {
      const response = await mergeIntakeDuplicateCandidate(lead.id, mergeCandidate.id, {
        reason: mergeReason,
        notes: mergeNotes
      });
      if (response.data.lead) onLeadUpdated(response.data.lead);
      showToast({ severity: 'success', summary: 'Merged', detail: 'Duplicate candidate merged; pipeline can continue' });
      setMergeCandidate(null);
      if (onRefreshLead) onRefreshLead();
    } catch (error) {
      console.error('Failed to merge duplicate lead:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to merge duplicate lead' });
    } finally {
      if (mountedRef.current) setMerging(false);
    }
  };

  const clearCandidate =

  async (row) => {
    setClearingId(row.id);
    try {
      const response = await resolveIntakeDuplicateCandidate(lead.id, row.id, {
        status: 'not_duplicate',
        reason: `Cleared duplicate candidate lead #${row.candidate_lead_id}`
      });
      if (response.data.lead) onLeadUpdated(response.data.lead);
      showToast({ severity: 'success', summary: 'Cleared', detail: 'Duplicate candidate marked not duplicate' });
      if (onRefreshLead) onRefreshLead();
    } catch (error) {
      console.error('Failed to clear duplicate candidate:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to clear duplicate candidate' });
    } finally {
      if (mountedRef.current) setClearingId(null);
    }
  };

  const mergeLabel = (() => {
    const candidate = mergeCandidate?.candidate_lead || {};
    return candidate.prospect_name || candidate.lead_title || `Lead #${mergeCandidate?.candidate_lead_id}`;
  })();

  return (
    <>
    <section className="intake-duplicate-alert" aria-label="Potential duplicate leads">
      <div className="intake-duplicate-alert__icon"><AppIcon icon="pi pi-exclamation-triangle" /></div>
      <div className="intake-duplicate-alert__body">
        <strong>Duplicate Review Required</strong>
        <span>{stateDetail}</span>
        <div className="intake-duplicate-alert__list">
          {rows.map(

          (row) => {
          const candidate = row.candidate_lead || {};
	          const label = candidate.prospect_name || candidate.lead_title || `Lead #${row.candidate_lead_id}`;
	          const latestCandidateRun = getLatestDedupeAgentRunForCandidate(agentRuns, row.id);
	          const agentOutcome = agentOutcomes[row.id] || latestCandidateRun;
	          const agentOutcomeText = getAgentOutcomeText(agentOutcome);
          return (
            <div className="intake-duplicate-alert__candidate" key={row.id || row.candidate_lead_id}>
              <div>
                <Link to={`/intake/leads/${row.candidate_lead_id}`}>
                  {label} · score {row.score}
                </Link>
                <small>{titleize(row.status)}{row.reason_codes?.length ? ` / ${row.reason_codes.map(titleize).join(', ')}` : ''}</small>
                {agentOutcomeText && <small>{agentOutcomeText}</small>}
              </div>
              <div className="intake-duplicate-alert__actions">
                <button
                  type="button"
                  className="intake-button"
                  onClick={() => requestAgentReview(row)}
                  disabled={!canRunDedupeAgent || agentReviewId === row.id || clearingId === row.id || merging}
                  title={!canRunDedupeAgent ? dedupeUnavailableReason : undefined}>
                  <AppIcon icon="pi pi-send" aria-hidden="true" />
                  <span>{!canRunDedupeAgent ? 'Dedupe Agent Unavailable' : agentReviewId === row.id ? 'Running' : agentOutcome && FAILED_AGENT_STATUSES.has(agentOutcome.status) ? 'Retry Dedupe Agent' : agentOutcome && ACTIVE_AGENT_STATUSES.has(agentOutcome.status) ? 'Dedupe Agent Queued' : 'Run Dedupe Agent'}</span>
                </button>
                <button type="button" className="intake-button" onClick={() => clearCandidate(row)} disabled={clearingId === row.id || merging}>
                  <AppIcon icon="pi pi-check" aria-hidden="true" />
                  <span>{clearingId === row.id ? 'Clearing' : 'Not Duplicate'}</span>
                </button>
                <button type="button" className="intake-button intake-button--primary" onClick={() => openMergeModal(row)} disabled={merging || clearingId === row.id || lead.status === 'duplicate'}>
                  <AppIcon icon="pi pi-sitemap" aria-hidden="true" />
                  <span>Merge</span>
                </button>
              </div>
            </div>);

        })}
        </div>
      </div>
    </section>
    <AppModal
      visible={Boolean(mergeCandidate)}
      title="Merge Duplicate Lead"
      description={`Merge details from ${mergeLabel} and clear the duplicate blocker.`}
      onClose={() => setMergeCandidate(null)}
      footer={
        <button type="button" className="intake-button intake-button--primary" onClick={confirmMerge} disabled={merging || !mergeReason.trim()}>
          <AppIcon icon="pi pi-sitemap" aria-hidden="true" />
          <span>{merging ? 'Merging' : 'Merge Lead'}</span>
        </button>
      }
      width="min(640px, calc(100vw - 2rem))">

      <div className="intake-stack">
        <section className="intake-decision-panel">
          <div className="intake-decision-grid intake-decision-grid--single">
            <label className="intake-form-field">
              <span>Reason</span>
              <input value={mergeReason} onChange={(event) => setMergeReason(event.target.value)} />
            </label>
            <label className="intake-form-field intake-form-field--wide">
              <span>Notes</span>
              <textarea rows={4} value={mergeNotes} onChange={(event) => setMergeNotes(event.target.value)} />
            </label>
          </div>
        </section>
      </div>
    </AppModal>
    </>);

}



function getRejectionEmailForm(lead, outreach) {
  const fieldOrFallback =

  (field, fallback = '') => {
    if (!outreach) return fallback || '';
    const value = outreach[field];
    return value === null || value === undefined ? fallback || '' : value;
  };
  return {
    recipient_name: fieldOrFallback('recipient_name', lead?.prospect_name),
    recipient_email: fieldOrFallback('recipient_email', lead?.prospect_email),
    recipient_phone: fieldOrFallback('recipient_phone', lead?.prospect_phone),
    subject: fieldOrFallback('subject'),
    body: fieldOrFallback('body')
  };
}



function RejectionEmailReviewModal({ visible, lead, outreach, deliveryPolicy, onLeadUpdated, onRefreshLead, onDone }) {
  const showToast = useContext(ToastContext);
  const [row, setRow] = useState(outreach || null);
  const [policy, setPolicy] = useState(deliveryPolicy || null);
  const [form, setForm] = useState(() => getRejectionEmailForm(lead, outreach));
  const [saving, setSaving] = useState(false);
  const [sendingTarget, setSendingTarget] = useState(null);

  useEffect(

  () => {
    if (!visible) return;
    setRow(outreach || null);
    setPolicy(deliveryPolicy || null);
    setForm(getRejectionEmailForm(lead, outreach));
    setSaving(false);
    setSendingTarget(null);
  }, [visible, lead, outreach, deliveryPolicy]);

  const updateForm =

  (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveDraft =

  async (silent = false) => {
    if (!row?.id || saving || sendingTarget) return null;
    setSaving(true);
    try {
      const response = await updateIntakeOutreach(lead.id, row.id, {
        message_type: 'email',
        recipient_name: form.recipient_name,
        recipient_email: form.recipient_email,
        recipient_phone: form.recipient_phone,
        subject: form.subject,
        body: form.body
      });
      const savedRow = response.data.outreach;
      setRow(savedRow);
      if (response.data.delivery_policy) setPolicy(response.data.delivery_policy);
      setForm(getRejectionEmailForm(lead, savedRow));
      if (!silent) showToast({ severity: 'success', summary: 'Saved', detail: 'Rejection email draft saved' });
      return savedRow;
    } catch (error) {
      console.error('Failed to save rejection outreach draft:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to save rejection email draft' });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const sendDraft =

  async (sendTarget) => {
    const currentDevDeliveryTarget = policy?.redirect_email || (
      String(policy?.mode || '').toLowerCase() === 'redirect' ? policy?.delivered_recipient_email : ''
    );
    if (!row?.id || !form.recipient_email || !form.subject || !form.body || saving || sendingTarget) return;
    if (sendTarget === 'dev' && !currentDevDeliveryTarget) return;
    const savedRow = await saveDraft(true);
    if (!savedRow) return;

    setSendingTarget(sendTarget);
    try {
      const response = await sendIntakeOutreach(lead.id, savedRow.id, { send_target: sendTarget });
      const sentPolicy = response.data.delivery_policy || response.data.delivery || policy;
      if (response.data.delivery_policy) setPolicy(response.data.delivery_policy);
      if (response.data.lead) {
        onLeadUpdated?.(response.data.lead);
      } else {
        await onRefreshLead?.();
      }
      setRow(response.data.outreach || savedRow);
      showToast(getOutreachSendToast(sentPolicy, form.recipient_email));
      onDone?.();
    } catch (error) {
      console.error('Failed to send rejection outreach email:', error);
      const failedRow = error?.response?.data?.outreach;
      if (failedRow) setRow(failedRow);
      if (error?.response?.data?.delivery_policy) setPolicy(error.response.data.delivery_policy);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to send rejection email' });
    } finally {
      setSendingTarget(null);
    }
  };

  const closeModal =

  () => {
    if (saving || sendingTarget) return;
    onDone?.();
  };

  const requiredSendFieldsPresent = Boolean(form.recipient_email && form.subject && form.body);
  const deliveryNotice = getOutreachDeliveryNotice(policy, form.recipient_email);
  const devDeliveryTarget = policy?.redirect_email || (
    String(policy?.mode || '').toLowerCase() === 'redirect' ? policy?.delivered_recipient_email : ''
  );
  const sendBlocked = !row?.id || !requiredSendFieldsPresent || saving || Boolean(sendingTarget);
  const canSendToDev = !sendBlocked && Boolean(devDeliveryTarget);
  const canSendToActual = !sendBlocked;

  const footer = (
    <>
      <button type="button" className="intake-button" disabled={!row?.id || saving || Boolean(sendingTarget)} onClick={() => saveDraft(false)}>
        <AppIcon icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-save'} aria-hidden="true" />
        <span>{saving ? 'Saving' : 'Save Draft'}</span>
      </button>
      {devDeliveryTarget &&
      <button type="button" className="intake-button" disabled={!canSendToDev} onClick={() => sendDraft('dev')}>
        <AppIcon icon={sendingTarget === 'dev' ? 'pi pi-spin pi-spinner' : 'pi pi-send'} aria-hidden="true" />
        <span>{sendingTarget === 'dev' ? 'Sending' : 'Send to Dev Account'}</span>
      </button>}
      <button type="button" className="intake-button intake-button--primary" disabled={!canSendToActual} onClick={() => sendDraft('actual')}>
        <AppIcon icon={sendingTarget === 'actual' ? 'pi pi-spin pi-spinner' : 'pi pi-send'} aria-hidden="true" />
        <span>{sendingTarget === 'actual' ? 'Sending' : 'Send to Actual Person'}</span>
      </button>
    </>
  );

  return (
    <AppModal
      visible={visible}
      title="Review Rejection Email"
      description="Approve or edit the rejection notice before sending it to the prospect."
      onClose={closeModal}
      footer={footer}
      width="min(720px, calc(100vw - 2rem))"
      className="intake-decision-modal">
      <div className="intake-stack">
        <section className="intake-decision-panel">
          <div className="intake-form-grid">
            <TextInput label="To" value={form.recipient_email} disabled={saving || Boolean(sendingTarget)} onChange={(value) => updateForm('recipient_email', value)} />
            <TextInput label="Subject" value={form.subject} disabled={saving || Boolean(sendingTarget)} onChange={(value) => updateForm('subject', value)} />
            <TextArea label="Message" value={form.body} rows={10} disabled={saving || Boolean(sendingTarget)} onChange={(value) => updateForm('body', value)} />
          </div>
          {deliveryNotice &&
          <div className={`intake-draft-delivery-notice intake-draft-delivery-notice--${deliveryNotice.tone}`}>
            <AppIcon icon={deliveryNotice.icon} aria-hidden="true" />
            <div>
              <strong>{deliveryNotice.title}</strong>
              <span>{deliveryNotice.detail}</span>
            </div>
          </div>}
        </section>
      </div>
    </AppModal>
  );
}



function AttorneyReviewHandoffModal({ visible, lead, onClose, onConfirmed }) {
  const showToast = useContext(ToastContext);
  const [handoff, setHandoff] = useState(null);
  const [attorneyId, setAttorneyId] = useState('');
  const [handoffNote, setHandoffNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(

  () => {
    if (!visible || !lead?.id) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setHandoff(null);
    setAttorneyId('');
    setHandoffNote('');
    fetchIntakeAttorneyReviewHandoff(lead.id)
      .then(

      (response) => {
        if (cancelled) return;
        const nextHandoff = response.data || {};
        setHandoff(nextHandoff);
        setAttorneyId(nextHandoff.proposed_attorney?.attorney_id ? String(nextHandoff.proposed_attorney.attorney_id) : '');
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError?.response?.data?.error || 'Unable to load the Case Review handoff.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [lead?.id, visible]);

  const submit =

  async () => {
    if (!attorneyId || !handoffNote.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      const response = await confirmIntakeAttorneyReviewHandoff(lead.id, {
        attorney_id: Number(attorneyId),
        handoff_note: handoffNote.trim(),
      });
      showToast({
        severity: 'success',
        summary: 'Sent to Case Review',
        detail: `Case Review assigned to ${response.data?.attorney_review?.attorney_name || response.data?.attorney_review?.attorney_email}.`,
      });
      onConfirmed?.(response.data || {});
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Unable to create the Case Review assignment.');
    } finally {
      setSaving(false);
    }
  };

  const context = handoff?.context || {};
  const proposed = handoff?.proposed_attorney;
  const attorneys = handoff?.attorneys || [];
  const footer = (
    <>
      <button type="button" className="intake-button" disabled={saving} onClick={onClose}>Cancel</button>
      <button
        type="button"
        className="intake-button intake-button--primary"
        disabled={loading || saving || !attorneyId || !handoffNote.trim()}
        onClick={submit}>
        <AppIcon icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-send'} aria-hidden="true" />
        <span>{saving ? 'Assigning' : 'Create Case Review assignment'}</span>
      </button>
    </>
  );

  return (
    <AppModal
      visible={visible}
      title="Send to Case Review"
      description={`Confirm the attorney handoff from ${context.stage_label || titleize(lead?.current_stage) || 'the current Intake stage'}.`}
      onClose={saving ? undefined : onClose}
      footer={footer}
      width="min(760px, calc(100vw - 2rem))"
      className="intake-decision-modal intake-attorney-handoff-modal">
      {loading ? <div className="intake-loading"><AppSpinner /></div> : (
        <div className="intake-stack">
          <section className="intake-decision-panel intake-attorney-handoff-context">
            <div className="intake-section-heading">
              <h2>{context.stage_label || titleize(context.stage_key) || 'Current stage'}</h2>
              <span>{titleize(context.lead_status || lead?.status)}</span>
            </div>
            <p>{context.staff_review_reason || context.next_action_label || 'Review the current Intake context before assigning Case Review.'}</p>
            <dl className="intake-attorney-handoff-facts">
              <div><dt>Claim</dt><dd>{context.claim_type || '-'}</dd></div>
              <div><dt>Defendant</dt><dd>{context.defendant || '-'}</dd></div>
              <div><dt>State / Forum</dt><dd>{context.state_or_forum || '-'}</dd></div>
              <div><dt>Documents</dt><dd>{Number(context.document_count || 0)}</dd></div>
            </dl>
            {context.final_recommendation?.summary && (
              <div className="intake-attorney-handoff-recommendation">
                <span>Final Evaluation</span>
                <strong>{context.final_recommendation.summary}</strong>
              </div>
            )}
          </section>
          <section className="intake-decision-panel">
            {proposed?.attorney_id && (
              <div className="intake-inline-alert intake-inline-alert--success" role="status">
                <AppIcon icon="pi pi-sparkles" aria-hidden="true" />
                <span>Final Evaluation proposed <strong>{proposed.attorney_name || proposed.attorney_email}</strong>. You can keep or change this selection.</span>
              </div>
            )}
            <div className="intake-form-grid">
              <SelectInput
                label="Case Review attorney"
                value={attorneyId}
                disabled={saving}
                onChange={setAttorneyId}
                options={[
                  ['', 'Choose an attorney'],
                  ...attorneys.map((attorney) => [String(attorney.id), `${attorney.name || attorney.email}${attorney.name ? ` · ${attorney.email}` : ''}`]),
                ]} />
              <TextArea
                label="Kimberly's handoff note"
                value={handoffNote}
                rows={5}
                disabled={saving}
                onChange={setHandoffNote} />
            </div>
            {error && <div className="intake-form-error" role="alert">{error}</div>}
          </section>
        </div>
      )}
    </AppModal>
  );
}



function IntakeLeadDetail() {
  const { leadId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useContext(ToastContext);
  const { isAdmin = false } = useContext(AuthContext) || {};
  const [payload, setPayload] = useState(null);
  const [pipelinePayload, setPipelinePayload] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineError, setPipelineError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decisionModal, setDecisionModal] = useState({ visible: false, decision: 'accepted' });
  const [attorneyHandoffVisible, setAttorneyHandoffVisible] = useState(false);
  const [rejectionEmailModal, setRejectionEmailModal] = useState({ visible: false, outreach: null, deliveryPolicy: null });
  const [reingestingPipedrive, setReingestingPipedrive] = useState(false);
  const [resettingPipeline, setResettingPipeline] = useState(false);
  const [resetDialogVisible, setResetDialogVisible] = useState(false);
  const [resetReason, setResetReason] = useState('');
  const [resetHold, setResetHold] = useState(false);
  const [engagementOverrideVisible, setEngagementOverrideVisible] = useState(false);
  const [engagementOverrideReason, setEngagementOverrideReason] = useState('');
  const [engagementOverrideConfirmed, setEngagementOverrideConfirmed] = useState(false);
  const [overridingEngagement, setOverridingEngagement] = useState(false);
  const headerMenuRef = useRef(null);
  const closeHeaderMenu =

  () => headerMenuRef.current?.removeAttribute('open');

  const loadLead = useCallback(

  (options = {}) => {
    const silent = Boolean(options?.silent);
    if (!silent) setLoading(true);
    setError(null);
    return fetchIntakeLeadOverview(leadId)
    .then((response) => {
      setPayload(response.data);
    })
    .catch((err) => {
      console.error('Failed to load intake lead:', err);
      if (!silent) {
        setError('Lead not found or failed to load.');
        showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load intake lead' });
      }
    })
    .finally(() => {
      if (!silent) setLoading(false);
    });
  }, [leadId, showToast]);

  const refreshLeadSilently = useCallback(

  () => loadLead({ silent: true }), [loadLead]);

  useEffect(() => {
    setPipelinePayload(null);
    setPipelineError('');
  }, [leadId]);

  useEffect(() => {
    loadLead();
  }, [loadLead]);

  const loadPipeline = useCallback(

  (options = {}) => {
    if (!leadId) return Promise.resolve();
    setPipelineLoading(true);
    if (options?.clear) setPipelinePayload(null);
    setPipelineError('');
    return fetchIntakeLeadPipeline(leadId)
    .then((response) => {
      setPipelinePayload(response.data);
    })
    .catch((err) => {
      console.error('Failed to load intake lead pipeline:', err);
      setPipelinePayload(null);
      setPipelineError('Pipeline failed to load.');
    })
    .finally(() => {
      setPipelineLoading(false);
    });
  }, [leadId]);

  const handleLeadUpdated = useCallback(

  (updatedLead, responsePayload = null) => {
    setPayload((current) => {
      if (!current) return current;
      return { ...current, lead: { ...current.lead, ...updatedLead } };
    });
    if (responsePayload) {
      setPipelinePayload((current) => mergeRunResponseIntoPayload(current || {}, responsePayload));
    }
  }, []);

  const handleDecisionRecorded = useCallback(

  (decisionPayload = {}) => {
    if (!decisionPayload.rejection_outreach) return;
    setRejectionEmailModal({
      visible: true,
      outreach: decisionPayload.rejection_outreach,
      deliveryPolicy: decisionPayload.rejection_outreach_delivery_policy || decisionPayload.delivery_policy || null
    });
  }, []);

  const returnToInbox = new URLSearchParams(location.search).get('from') === 'inbox';
  const returnPath = returnToInbox ? '/inbox' : getLastIntakeWorkspacePath();
  const returnLabel = returnToInbox ? 'Back to Inbox' : 'Back to Intake';

  const closeRejectionEmailModal = useCallback(

  () => {
    setRejectionEmailModal({ visible: false, outreach: null, deliveryPolicy: null });
    navigate(returnPath);
  }, [navigate, returnPath]);

  const lead = payload?.lead;

  if (loading) {
    return (
      <div className="intake-page">
                <div className="intake-loading"><AppSpinner /></div>
            </div>);

  }

  if (error || !payload?.lead) {
    return (
      <div className="intake-page intake-detail-page">
                <div className="intake-page__body">
                    <div className="intake-detail-view">
                        <header className="intake-detail-page-header kc-layout-group">
                            <button type="button" className="intake-button" onClick={() => navigate(returnPath)}>
                                <AppIcon icon="pi pi-arrow-left" aria-hidden="true" />
                                <span>{returnLabel}</span>
                            </button>
                            <div className="intake-detail-title">
                                <span className="intake-detail-title__eyebrow">Intake</span>
                                <h1>Lead Not Found</h1>
                            </div>
                        </header>
                        <section className="intake-detail-panel">
                            <div className="intake-error">{error || 'Lead not found.'}</div>
                        </section>
                    </div>
                </div>
            </div>);

  }

  const {
    duplicate_candidates = [],
    dedupe_agent_status = null,
    conversion_preview = null
  } = payload;
  const {
    pipeline_runs = [],
    stage_runs = [],
    agent_runs = [],
    final_recommendations = [],
    ai_spending = null,
    pipeline_version = null,
    latest_pipeline_version = null,
	    pipeline_stages = [],
	    model_profiles = [],
	    can_upgrade_pipeline = false,
	    workflow_rail = {},
	  } = pipelinePayload || {};
  const convertedMatter = lead.converted_matter_number || conversion_preview?.converted_matter_number;
  const pipedriveHandoff = isPipedriveHandoffMode(conversion_preview?.conversion_mode);
  const previewMatter = pipedriveHandoff ? convertedMatter : convertedMatter || conversion_preview?.matter_number;
  const conversionBlocker = getConversionBlocker(conversion_preview, convertedMatter, false, '');
  const openDecisionModal =

  (decision = 'accepted') => setDecisionModal({ visible: true, decision });
	  const pendingAttorneyReview = (payload.attorney_review_assignments || []).find((assignment) => assignment.status === 'pending');
	  const approvedAttorneyReview = (payload.attorney_review_assignments || []).find((assignment) => assignment.status === 'approved');
  const canReingestPipedrive = Boolean(lead.pipedrive_lead_id);
  const reingestPipedriveTitle = canReingestPipedrive ? 'Refresh this lead from Pipedrive' : 'This lead is not linked to a Pipedrive lead';
  const canResetPipeline = !lead.converted_matter_number && lead.status !== 'converted';
  const resetPipelineTitle = canResetPipeline ? 'Clear pipeline runs and restart this lead at the first pipeline stage' : 'Converted leads cannot be reset from Intake';
  const resetVersionLabel = getPipelineVersionLabel(latest_pipeline_version || pipeline_version);
  const heldAfterReset = lead.status === 'waiting_on_prospect' && lead.staff_review_reason === RESET_HOLD_REASON;
  const resolvedLead = isResolvedIntakeLead(lead);
  const presentationState = getLeadPresentationState(lead, { hasPendingAttorneyReview: Boolean(pendingAttorneyReview) });
  const engagementStage = (workflow_rail.stages || []).find((stage) => stage.key === 'engagement_package');
  const engagementBypassed = engagementStage?.engagement_draft?.status === 'bypassed';
  const canOverrideEngagement = Boolean(
    isAdmin &&
    lead.final_decision === 'accepted' &&
    ['accepted', 'engagement_letter_to_be_sent'].includes(lead.status) &&
    !lead.converted_matter_number &&
    !engagementBypassed
  );
  const awaitingAttorneyReview = presentationState === 'awaiting_attorney_review';
  const showDuplicateBadge = Boolean(
    lead.duplicate_review_status &&
    lead.duplicate_review_status !== 'not_duplicate'
  );
  const useStageAsPrimaryState = !resolvedLead &&
    !awaitingAttorneyReview &&
    ['new', 'processing'].includes(lead.status) &&
    Boolean(lead.current_stage);
	  const primaryStateLabel = awaitingAttorneyReview
	    ? 'Awaiting Attorney Decision'
	    : lead.status === 'engagement_letter_to_be_sent'
	    ? 'Attorney Accepted — Engagement Pending'
    : resolvedLead
    ? lead.status === 'converted' && lead.converted_matter_number
      ? `Converted to Matter ${lead.converted_matter_number}`
      : titleize(lead.status)
    : useStageAsPrimaryState
    ? titleize(lead.current_stage)
    : titleize(lead.status || lead.current_stage);
  const primaryStateClass = awaitingAttorneyReview
    ? 'intake-badge--info'
    : useStageAsPrimaryState
    ? getStageClass(lead.current_stage)
    : getStatusClass(lead.status);
	  const operationalIntakeLead = presentationState === 'active';
	  const canSendToCaseReview = operationalIntakeLead &&
	    !approvedAttorneyReview &&
	    lead.status !== 'engagement_letter_to_be_sent';
  const handlePipedriveReingest =

  async () => {
    if (!canReingestPipedrive || reingestingPipedrive) return;
    setReingestingPipedrive(true);
    try {
      const response = await reingestIntakeLeadFromPipedrive(lead.id);
      if (response.data.lead) handleLeadUpdated(response.data.lead);
      await loadLead({ silent: true });
      const result = response.data.result || {};
      const changed = Number(result.updated || 0);
      const detail = changed ? 'Lead updated from Pipedrive.' : 'No Pipedrive lead changes found.';
      showToast({ severity: result.status === 'partial_success' ? 'warn' : 'success', summary: 'Pipedrive Re-ingest Complete', detail });
    } catch (err) {
      console.error('Failed to re-ingest intake lead from Pipedrive:', err);
      showToast({ severity: 'error', summary: 'Pipedrive Re-ingest Failed', detail: err?.response?.data?.error || 'Failed to refresh from Pipedrive' });
    } finally {
      setReingestingPipedrive(false);
    }
  };
  const openResetPipelineDialog =

  () => {
    if (!canResetPipeline || resettingPipeline) return;
    setResetReason('');
    setResetHold(false);
    setResetDialogVisible(true);
  };
  const closeResetPipelineDialog =

  () => {
    if (!resettingPipeline) setResetDialogVisible(false);
  };
  const confirmResetPipeline =

  async () => {
    if (!canResetPipeline || resettingPipeline) return;
    setResettingPipeline(true);
    try {
      const response = await resetIntakeLeadPipeline(lead.id, {
        reason: resetReason.trim() || DEFAULT_RESET_REASON,
        hold: resetHold,
      });
      if (response.data.lead) handleLeadUpdated(response.data.lead);
      await Promise.all([
        loadLead({ silent: true }),
        loadPipeline({ clear: true })
      ]);
      setResetDialogVisible(false);
      showToast({ severity: 'success', summary: 'Pipeline Reset', detail: resetHold ? 'Lead reset and held until staff resumes it.' : 'Lead reset to the first intake stage.' });
    } catch (err) {
      console.error('Failed to reset intake lead pipeline:', err);
      showToast({ severity: 'error', summary: 'Pipeline Reset Failed', detail: err?.response?.data?.error || 'Failed to reset pipeline' });
    } finally {
      setResettingPipeline(false);
    }
  };

  const openEngagementOverride =

  () => {
    setEngagementOverrideReason('');
    setEngagementOverrideConfirmed(false);
    setEngagementOverrideVisible(true);
  };
  const closeEngagementOverride =

  () => {
    if (!overridingEngagement) setEngagementOverrideVisible(false);
  };
  const confirmEngagementOverride =

  async () => {
    const reason = engagementOverrideReason.trim();
    if (!reason || !engagementOverrideConfirmed || overridingEngagement) return;
    setOverridingEngagement(true);
    try {
      const response = await overrideIntakeEngagementPackage(lead.id, { reason });
      if (response.data.lead) handleLeadUpdated(response.data.lead);
      await Promise.all([
        loadLead({ silent: true }),
        loadPipeline({ clear: true }),
      ]);
      setEngagementOverrideVisible(false);
      const conversion = response.data.conversion || {};
      showToast({
        severity: 'success',
        summary: 'Engagement package bypassed',
        detail: isPipedriveHandoffMode(conversion.mode)
          ? 'The accepted lead continued through the Pipedrive handoff.'
          : conversion.matter_number
          ? `Matter ${conversion.matter_number} was created.`
          : 'The accepted lead continued through conversion.',
      });
    } catch (overrideError) {
      console.error('Failed to override engagement package:', overrideError);
      showToast({
        severity: 'error',
        summary: 'Override failed',
        detail: overrideError?.response?.data?.error || 'Could not bypass the engagement package.',
      });
    } finally {
      setOverridingEngagement(false);
    }
  };

  return (
    <div className="intake-page intake-detail-page">
            <div className="intake-page__body">
                <div className="intake-detail-view">
                    <header className="intake-detail-page-header kc-layout-group">
                        <div className="intake-detail-header-main">
                            <button type="button" className="intake-button" onClick={() => navigate(returnPath)}>
                                <AppIcon icon="pi pi-arrow-left" aria-hidden="true" />
                                <span>{returnToInbox ? 'Back to Inbox' : 'Back'}</span>
                            </button>
                            <div className="intake-detail-title">
                                <span className="intake-detail-title__eyebrow">Intake Lead #{lead.id}</span>
                                <h1>{lead.prospect_name || lead.lead_title || `Lead #${lead.id}`}</h1>
                            </div>
                        </div>
                        <div className="intake-detail-header-actions">
                            {getPipedriveRecordUrl(lead) ? <a className="intake-button" href={getPipedriveRecordUrl(lead)} target="_blank" rel="noopener noreferrer">
                              <AppIcon icon="pi pi-external-link" aria-hidden="true" /> Open in Pipedrive
                            </a> : null}
                            <div className="intake-detail-status-actions">
                                {operationalIntakeLead && <details className="intake-header-menu" ref={headerMenuRef}>
                                    <summary className="intake-button" aria-label="More lead actions" aria-haspopup="menu">
                                        <AppIcon icon="pi pi-ellipsis-h" aria-hidden="true" />
                                    </summary>
                                    <div className="intake-header-menu__panel" role="menu" onClick={closeHeaderMenu}>
                                        <button
                                          type="button"
                                          className="intake-button"
                                          onClick={handlePipedriveReingest}
                                          disabled={!canReingestPipedrive || reingestingPipedrive}
                                          title={reingestPipedriveTitle}>
                                          <AppIcon icon="pi pi-refresh" aria-hidden="true" />
                                          <span>{reingestingPipedrive ? 'Re-ingesting' : 'Re-ingest from Pipedrive'}</span>
                                        </button>
                                        <button
                                          type="button"
                                          className="intake-button intake-button--danger"
                                          onClick={openResetPipelineDialog}
                                          disabled={!canResetPipeline || resettingPipeline}
                                          title={resetPipelineTitle}>
                                          <AppIcon icon="pi pi-undo" aria-hidden="true" />
                                          <span>{resettingPipeline ? 'Resetting' : 'Reset Pipeline'}</span>
                                        </button>
                                    </div>
                                </details>}
                                <div className="intake-detail-badges">
                                    <span className={`intake-badge ${primaryStateClass}`}>{primaryStateLabel}</span>
                                    {heldAfterReset && <span className="intake-badge intake-badge--warning">Held After Reset</span>}
                                    {showDuplicateBadge && <span className={`intake-badge ${getDuplicateStatusClass(lead.duplicate_review_status)}`}>{getDuplicateStatusLabel(lead)}</span>}
                                    {lead.potential_class_action && <span className="intake-badge intake-badge--violet">Potential Class Action</span>}
                                </div>
                            </div>
	                            {canSendToCaseReview && <div className="intake-decision-paths">
                              <button
                                type="button"
                                className="intake-button intake-button--primary"
                                onClick={() => setAttorneyHandoffVisible(true)}
                                disabled={Boolean(pendingAttorneyReview)}>
                                <AppIcon icon="pi pi-briefcase" aria-hidden="true" />
                                <span>{pendingAttorneyReview ? 'Case Review assigned' : 'Send to Case Review'}</span>
                              </button>
                              <details className="intake-decision-override">
                                <summary>Attorney override</summary>
                                <DecisionActionButtons
                              lead={lead}
                              onOpenDecision={openDecisionModal}
                              previewMatter={previewMatter}
                              conversionBlocker={conversionBlocker}
                              engagementLetterHandoff={pipedriveHandoff} />
                              </details>
                            </div>}
                        </div>
                    </header>

                    <div className="intake-detail-top-grid">
                        <ProspectPanel lead={lead} onLeadUpdated={handleLeadUpdated} />
                        <IntakeStatusPanel lead={lead} onLeadUpdated={handleLeadUpdated} />
                        {operationalIntakeLead && <PipelineStatusPanel workflowSummary={lead.workflow_summary} />}
                    </div>
                    <DuplicateCandidatesPanel
                      lead={lead}
                      candidates={duplicate_candidates}
                      dedupeAgentStatus={dedupe_agent_status}
                      agentRuns={agent_runs}
                      onLeadUpdated={handleLeadUpdated}
                      onRefreshLead={refreshLeadSilently} />

                    <section className="intake-detail-workspace">
                        {canOverrideEngagement && <div className="intake-engagement-override" role="region" aria-label="Engagement package administrator override">
                          <div className="intake-engagement-override__icon"><AppIcon icon="pi pi-shield" aria-hidden="true" /></div>
                          <div className="intake-engagement-override__copy">
                            <strong>Admin override</strong>
                            <span>Continue this accepted lead without completing the engagement package.</span>
                          </div>
                          <button type="button" className="intake-button intake-button--danger" onClick={openEngagementOverride}>
                            <AppIcon icon="pi pi-forward" aria-hidden="true" />
                            <span>{pipedriveHandoff ? 'Skip package & hand off to Pipedrive' : 'Skip package & create Matter'}</span>
                          </button>
                        </div>}
                        <IntakeTabs
              lead={lead}
              pipelineRuns={pipeline_runs}
              stageRuns={stage_runs}
              agentRuns={agent_runs}
              finalRecommendations={final_recommendations}
              aiSpending={ai_spending}
              engagementLetterHandoff={pipedriveHandoff}
	              pipelineVersion={pipeline_version}
	              latestPipelineVersion={latest_pipeline_version}
		              pipelineStages={pipeline_stages}
		              workflowRail={workflow_rail}
              modelProfiles={model_profiles}
              canUpgradePipeline={can_upgrade_pipeline}
              pipelineLoaded={Boolean(pipelinePayload)}
              loading={pipelineLoading}
              error={pipelineError}
              onLeadUpdated={handleLeadUpdated}
              onRefreshLead={refreshLeadSilently}
              onRefreshPipeline={loadPipeline}
              onOpenDecision={openDecisionModal}
              presentationState={presentationState}
              completedMode={resolvedLead} />

                    </section>
	                    <IntakeDecisionModal
	              lead={lead}
              initialPreview={conversion_preview}
	              visible={decisionModal.visible}
	              initialDecision={decisionModal.decision}
	              onClose={() => setDecisionModal((current) => ({ ...current, visible: false }))}
	              onLeadUpdated={handleLeadUpdated}
	              onDecisionRecorded={handleDecisionRecorded} />
                    <AttorneyReviewHandoffModal
                      visible={attorneyHandoffVisible}
                      lead={lead}
                      onClose={() => setAttorneyHandoffVisible(false)}
                      onConfirmed={

                      (responsePayload) => {
                        if (responsePayload.lead) handleLeadUpdated(responsePayload.lead);
                        setPayload((current) => current ? {
                          ...current,
                          attorney_review_assignments: responsePayload.attorney_review
                            ? [...(current.attorney_review_assignments || []), responsePayload.attorney_review]
                            : current.attorney_review_assignments,
                        } : current);
                        setAttorneyHandoffVisible(false);
                        loadPipeline();
                      }} />
                    <RejectionEmailReviewModal
                      visible={rejectionEmailModal.visible}
                      lead={lead}
                      outreach={rejectionEmailModal.outreach}
                      deliveryPolicy={rejectionEmailModal.deliveryPolicy}
                      onLeadUpdated={handleLeadUpdated}
                      onRefreshLead={refreshLeadSilently}
                      onDone={closeRejectionEmailModal} />
                    <AppModal
                      visible={resetDialogVisible}
                      title="Reset Intake Pipeline"
                      description="Clear current pipeline state and move this lead back to the first configured stage."
                      onClose={closeResetPipelineDialog}
                      width="min(720px, calc(100vw - 2rem))"
                      className="intake-decision-modal intake-reset-modal"
                      footer={
                        <>
                          <button type="button" className="intake-button" onClick={closeResetPipelineDialog} disabled={resettingPipeline}>
                            <AppIcon icon="pi pi-times" aria-hidden="true" />
                            <span>Cancel</span>
                          </button>
                          <button type="button" className="intake-button intake-button--danger" onClick={confirmResetPipeline} disabled={resettingPipeline}>
                            <AppIcon icon="pi pi-undo" aria-hidden="true" />
                            <span>{resettingPipeline ? 'Resetting' : 'Reset Pipeline'}</span>
                          </button>
                        </>
                      }>

                      <div className="intake-stack">
                        <section className="intake-decision-panel intake-reset-summary">
                          <div className="intake-reset-version">
                            <span>Reset Target</span>
                            <strong>{resetVersionLabel}</strong>
                          </div>
                          <p className="intake-reset-note">
                            Automation will restart the pipeline after reset unless Hold after reset is checked.
                          </p>
                          <div className="intake-reset-columns">
                            <div>
                              <h3>Cleared</h3>
                              <ul>
                                {RESET_CLEAR_ITEMS.map((item) => <li key={item}>{item}</li>)}
                              </ul>
                            </div>
                            <div>
                              <h3>Kept</h3>
                              <ul>
                                {RESET_KEEP_ITEMS.map((item) => <li key={item}>{item}</li>)}
                              </ul>
                            </div>
                          </div>
                        </section>
                        <section className="intake-decision-panel">
                          <div className="intake-decision-grid intake-decision-grid--single">
                            <label className="intake-form-field intake-form-field--wide">
                              <span>Reason</span>
                              <textarea
                                rows={3}
                                value={resetReason}
                                onChange={(event) => setResetReason(event.target.value)}
                                placeholder={DEFAULT_RESET_REASON} />
                            </label>
                            <label className="intake-checkbox-row intake-checkbox-row--boxed intake-reset-hold">
                              <input
                                type="checkbox"
                                checked={resetHold}
                                onChange={(event) => setResetHold(event.target.checked)} />
                              <span>Hold after reset</span>
                              <small>Keep automation paused until staff resumes the lead.</small>
                            </label>
                          </div>
                        </section>
                      </div>
                    </AppModal>
                    <AppModal
                      visible={engagementOverrideVisible}
                      title="Skip Engagement Package"
                      description="Administrator override for an attorney-accepted lead."
                      onClose={closeEngagementOverride}
                      width="min(680px, calc(100vw - 2rem))"
                      className="intake-decision-modal intake-engagement-override-modal"
                      footer={
                        <>
                          <button type="button" className="intake-button" onClick={closeEngagementOverride} disabled={overridingEngagement}>
                            <AppIcon icon="pi pi-times" aria-hidden="true" />
                            <span>Cancel</span>
                          </button>
                          <button
                            type="button"
                            className="intake-button intake-button--danger"
                            onClick={confirmEngagementOverride}
                            disabled={overridingEngagement || !engagementOverrideConfirmed || !engagementOverrideReason.trim()}>
                            <AppIcon icon={overridingEngagement ? 'pi pi-spin pi-spinner' : 'pi pi-forward'} aria-hidden="true" />
                            <span>{overridingEngagement ? 'Continuing' : pipedriveHandoff ? 'Skip & hand off' : 'Skip & create Matter'}</span>
                          </button>
                        </>
                      }>
                      <div className="intake-stack">
                        <section className="intake-decision-panel">
                          <div className="intake-engagement-override-modal__warning">
                            <AppIcon icon="pi pi-exclamation-triangle" aria-hidden="true" />
                            <p>{pipedriveHandoff
                              ? 'This skips agreement generation and moves the existing lead or deal into the normal Pipedrive conversion handoff. The Matter will still be created from the won-deal workflow.'
                              : 'This skips agreement generation and immediately creates the Matter using the existing local conversion workflow.'}</p>
                          </div>
                          <label className="intake-form-field intake-form-field--wide">
                            <span>Reason</span>
                            <textarea
                              rows={4}
                              required
                              value={engagementOverrideReason}
                              onChange={(event) => setEngagementOverrideReason(event.target.value)}
                              placeholder="Why is the engagement package being bypassed?" />
                          </label>
                          <label className="intake-checkbox-row intake-checkbox-row--boxed">
                            <input
                              type="checkbox"
                              checked={engagementOverrideConfirmed}
                              onChange={(event) => setEngagementOverrideConfirmed(event.target.checked)} />
                            <span>I understand this skips the engagement-package requirement.</span>
                          </label>
                        </section>
                      </div>
                    </AppModal>

		                </div>
            </div>
        </div>);

}

export default IntakeLeadDetail;
